import type { PropType } from 'vue';
import type { TModalProps } from './TModal.types';
import { merge } from 'es-toolkit/compat';
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from 'vue';
import { TIcon } from '../TIcon';
import { useStyles } from './TModal.styles';

const defaultProps = {
  size: 'medium',
};

export const TModal = defineComponent({
  name: 'TModal',
  props: {
    open: Boolean as PropType<TModalProps['open']>,
    featured: Boolean as PropType<TModalProps['featured']>,
    size: String as PropType<TModalProps['size']>,
    destroyOnClose: Boolean as PropType<TModalProps['destroyOnClose']>,
    nonBlocking: Boolean as PropType<TModalProps['nonBlocking']>,
  },
  emits: ['close', 'cancel'],
  setup(_props, { slots, attrs, emit, expose }) {
    const props = computed(() => merge({}, defaultProps, _props, attrs));
    const isActive = ref(props.value.open);

    const baseClass = useStyles();
    const classes = computed(() => [
      baseClass.value,
      `${baseClass.value}--${props.value.size}`,
      {
        [`${baseClass.value}--featured`]: props.value.featured,
        [`${baseClass.value}--active`]: isActive.value,
        [`${baseClass.value}--non-blocking`]: props.value.nonBlocking,
      },
    ]);

    const modal = ref<HTMLDialogElement | null>(null);
    const showModal = () => {
      isActive.value = true;
      if (props.value.nonBlocking) {
        modal.value?.show();
      } else {
        modal.value?.showModal();
      }
    };
    const close = () => {
      isActive.value = false;
      modal.value?.close();
    };
    watch(
      [() => props.value.open, modal],
      () => {
        if (modal.value && props.value.open) {
          showModal();
        } else {
          close();
        }
      },
      {
        immediate: true,
      },
    );

    const handleCancel = (event: Event) => {
      if (props.value.open) {
        event.preventDefault();
      } else {
        isActive.value = false;
      }
      emit('cancel');
    };
    onMounted(() => {
      if (modal.value) {
        modal.value.addEventListener('cancel', handleCancel);
      }
    });
    onUnmounted(() => {
      if (modal.value) {
        modal.value.removeEventListener('cancel', handleCancel);
      }
    });

    const handleClose = () => {
      if (!props.value.open) {
        close();
      }
      emit('close');
    };

    const handleClickOutside = (event: Event) => {
      if (event.target === modal.value) {
        if (!props.value.open) {
          close();
        }
        emit('close');
      }
    };

    expose({
      showModal,
      close,
    });

    const trigger = ref(0);
    watch(isActive, () => {
      if (!isActive.value && props.value.destroyOnClose) {
        trigger.value += 1;
      }
    });

    const ModalComponent = () => (
      <dialog
        ref={modal}
        class={classes.value}
        aria-labelledby="modal-header"
        aria-describedby="modal-body"
        aria-modal="true"
        onClick={handleClickOutside}
        key={trigger.value}
      >
        {slots.header && (
          <div id="modal-header" class={`${baseClass.value}__header`}>
            {slots.header()}
            {props.value.featured && (
              <button
                class={`${baseClass.value}__close-button`}
                onClick={handleClose}
                aria-label="Close"
              >
                <TIcon icon="x-mark" />
              </button>
            )}
          </div>
        )}
        {(slots.body || slots.default) && (
          <div id="modal-body" class={`${baseClass.value}__body`}>
            {(slots.body ?? slots.default)?.()}
          </div>
        )}
        {slots.footer && (
          <div class={`${baseClass.value}__footer`}>{slots.footer()}</div>
        )}
      </dialog>
    );

    if (props.value.nonBlocking) {
      return () => (
        <div>
          {props.value.nonBlocking && isActive.value && (
            <div class={`${baseClass.value}__backdrop`} />
          )}
          <ModalComponent />
        </div>
      );
    }
    return () => <ModalComponent />;
  },
});
